/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx, css } from '@emotion/react'
import { Fragment } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiHorizontalRule,
  EuiImage,
  EuiLink,
  EuiListGroup,
  EuiSpacer,
  EuiText,
  EuiTitle,
  useEuiTheme,
} from '@elastic/eui'

import { FeatureBulletListGroupItem } from '@modules/feature-bullet/FeatureBullet'

import messages from '../messages'

import CCMClustersTileHeader from './CCMClustersTileHeader'

import type { FC } from 'react'

const bulletPointMessages = defineMessages({
  featureBullet1: {
    id: 'ccm.featureBullet.monitor',
    defaultMessage: 'Monitor your cluster',
  },
  featureBullet2: {
    id: 'ccm.featureBullet.consume',
    defaultMessage: 'Easily consume cloud services',
  },
  featureBullet3: {
    id: 'ccm.featureBullet.troubleshoot',
    defaultMessage: 'Easy troubleshooting',
  },
  featureBullet4: {
    id: 'ccm.featureBullet.onboarding',
    defaultMessage: 'Streamlined onboarding optimized for your use case',
  },
})

const CCM_LEARN_MORE_URL = 'https://www.elastic.co/' // TODO: https://github.com/elastic/cloud/issues/142672 replace with actual URL

const EmptyConnectedClustersTile: FC = () => {
  const { formatMessage } = useIntl()

  const { euiTheme } = useEuiTheme()

  const featureBulletListStyles = css({
    display: 'flex',
    flexWrap: 'wrap',
    li: {
      marginTop: euiTheme.size.s,
      flex: '50%',
    },
  })

  return (
    <Fragment>
      <CCMClustersTileHeader />
      <EuiHorizontalRule />

      <EuiFlexGroup alignItems='center' gutterSize='xl' justifyContent='spaceBetween'>
        <EuiFlexItem grow={1}>
          <EuiImage
            size='s'
            src={'/static/images/cloud-connected-empty-tile.svg'}
            alt={formatMessage(messages.panelIllustration)}
          />
        </EuiFlexItem>
        <EuiFlexItem grow={4}>
          <EuiTitle size='s'>
            <h3>{formatMessage(messages.ccmClustersTileTitle)}</h3>
          </EuiTitle>

          <EuiSpacer size='m' />

          <EuiText size='s'>
            <p>
              {formatMessage(messages.zeroCCMClustersBody, {
                link: (content) => (
                  <EuiLink
                    href={CCM_LEARN_MORE_URL}
                    aria-label={formatMessage(messages.learnMoreAriaLabel)}
                    target='_blank'
                  >
                    {content}
                  </EuiLink>
                ),
              })}
            </p>
          </EuiText>
          <EuiSpacer size='s' />
          <EuiListGroup css={featureBulletListStyles}>
            {Object.entries(bulletPointMessages).map(([key, message]) => (
              <FeatureBulletListGroupItem key={key} feature={formatMessage(message)} />
            ))}
          </EuiListGroup>
        </EuiFlexItem>
      </EuiFlexGroup>
    </Fragment>
  )
}

export default EmptyConnectedClustersTile
